import React, { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogActions, DialogTitle, TextField, Grid, Button, IconButton, InputLabel, List, ListItemText, ListItem, Alert, AlertTitle } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/es'
import 'dayjs/locale/en'
import 'dayjs/locale/pt'
import type { Paycode } from 'repository/timekeeper/@types/PaycodeModel'
import type { PaycodeCombination } from 'repository/timekeeper/@types/PaycodeCombination'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'

type PaycodeCombinationWithPaycodes = Omit<PaycodeCombination, 'paycodes'> & {
    paycodes: Paycode[]
}

interface Props {
    open: boolean
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
    selectedRow: PaycodeCombinationWithPaycodes[]
}

const safePermissions = (): string[] => {
    try {
        const rawPermissions = localStorage.getItem('permissions')
        const permissions = rawPermissions ? JSON.parse(rawPermissions) : []
        return Array.isArray(permissions) ? permissions : []
    } catch {
        return []
    }
}

const InfoPaycodesCombination = ({ open, setOpen, selectedRow }: Props) => {
    const { t, i18n } = useTranslation()
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [dates, setDates] = useState<[Dayjs | null, Dayjs | null]>([null, null])
    const [selectedPaycodes, setSelectedPaycodes] = useState<Paycode[]>([])

    const language = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]
    const localDateFormat = language === 'en' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'

    useEffect(() => {
        if (open && selectedRow?.[0]) {
            setName(selectedRow[0].name)
            setDescription(selectedRow[0].description)
            setDates([
                selectedRow[0].start_date ? dayjs(selectedRow[0].start_date) : null,
                selectedRow[0].end_date ? dayjs(selectedRow[0].end_date) : null,
            ])
            setSelectedPaycodes(selectedRow[0].paycodes || [])
        }
    }, [open, selectedRow])

    const close = () => {
        setOpen(false)
        setName('')
        setDescription('')
        setDates([null, null])
        setSelectedPaycodes([])
    }

    const permissions = safePermissions()

    return (
        <Dialog
            maxWidth="sm"
            fullWidth
            open={open}
            onClose={close}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">{t('settings.paycodes_combination.dialog.titles.info')}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={close}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers>
                {!permissions.includes('timekeeper.GetPaycodesCombinations') && (
                    <Alert severity='warning'>
                        <AlertTitle>{t('general.alerts.missing_permission.title')}</AlertTitle>

                        <List dense>
                            <ListItem>
                                <ListItemText primary={'timekeeper.GetPaycodesCombinations'} />
                            </ListItem>
                        </List>
                    </Alert>
                )}
                <Grid container>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                        <TextField
                            id='input-name'
                            fullWidth
                            label={t('settings.paycodes_combination.dialog.inputs.name')}
                            value={name}
                            disabled
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1.5 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language}>
                            <DateRangePicker
                                localeText={{
                                    start: t('settings.paycodes_combination.dialog.inputs.effective_date_from'),
                                    end: t('settings.paycodes_combination.dialog.inputs.effective_date_until'),
                                }}
                                value={dates}
                                format={localDateFormat}
                                readOnly
                                disabled
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1, mb: 1, maxHeight: 300 }}>
                        <InputLabel>{t('settings.paycodes_combination.dialog.inputs.paycodes')}</InputLabel>
                        {selectedPaycodes.length === 0 ? (
                            <List sx={{ width: '100%', bgcolor: 'background.paper', overflow: 'auto', maxHeight: 200 }}>
                                <ListItem>
                                    <ListItemText primary={t('settings.paycodes_combination.dialog.inputs.autocomplete.no_options')} />
                                </ListItem>
                            </List>
                        ) : (
                            <List sx={{ width: '100%', bgcolor: 'background.paper', overflow: 'auto', maxHeight: 200 }}>
                                {selectedPaycodes.map((data) => (
                                    <ListItem key={data.paycode_id}>
                                        <ListItemText primary={data.name} />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }} sx={{ mt: 1 }}>
                        <TextField
                            id='filled-textarea'
                            fullWidth
                            label={t('settings.paycodes_combination.dialog.inputs.description')}
                            value={description}
                            multiline
                            disabled
                            slotProps={{
                                input: {
                                    readOnly: true,
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant='contained' color='primary' onClick={close}>
                    {t('general.buttons.close')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export { InfoPaycodesCombination }
